/**
 * Ported VERBATIM from apr_template `src/pages/RoadmapSchedule.tsx` (Planning & Integration).
 * Only import paths change, plus anything noted inline as a PORT NOTE.
 */
import React, { useState, useEffect, useCallback } from 'react';
import PageHeader from '../compat/PageHeader';
import { authFetch } from '../compat/api';
import './ApplicationDependencies.css';
import './RoadmapScheduleGantt.css';
import EditScheduleModal from '../compat/EditScheduleModal';
import HelpPanel from '../compat/HelpPanel';
import { DispositionChip } from '../compat/DispositionChip';
import type { Help } from '../compat/helpTypes';
import { dayKey, daysBetween, holidaysBetween, parseDay } from '../compat/ganttCalendar';
// Decision Workspace design (owner ruling 2026-10-21): kit icons, banners and token pairs.
import { Banner, Icon, IconButton, icons } from '@platform/ui-kit/workspace';
import { WS_CHIP, WS_COLOR, WS_RADIUS, WS_STATUS, wsTone, wsType } from '../workspace/ws';
import type { WsTone } from '../workspace/ws';
import { GroupMultiSelect, groupFetchKey } from '../workspace/GroupMultiSelect';
import RoadmapReview from './RoadmapReview';
import { PHASES as RV_PHASES, PHASE_COLORS as RV_PHASE_COLORS, businessDays, calendarDays, fmtMDY, phaseLabel } from '../compat/roadmapPhases';
// The table takes Roadmap Visualization's design (owner request 2026-11-04) — its own stylesheet.
import './ImplementationRoadmap.css';

const RoadmapScheduleHelp: Help = {
  title: 'Roadmap Schedule',
  icon: '📅',
  purpose: 'Calculate and manage planned start/end dates, effort, duration, and phase timing for the applications in an integration group.',
  doHere: [
    'Select one or more <b>Integration Groups</b> (Ctrl-click for several) to load their schedule.',
    'Click <b>Calculate Schedule</b> to get a <b>proposed</b> roadmap from effort, dependencies and the group start date. Nothing is saved yet: it opens the <b>Roadmap Review</b>.',
    'In the review, click an application to adjust any phase date &mdash; or enter the roadmap by hand. The rule check (phase order, effort, dependencies, group start, locked apps) runs on every change.',
    'Click <b>Run AI review</b> for a feasibility verdict &mdash; Approved, Approved with conditions, or Not feasible &mdash; with the issues and what to change.',
    'Click <b>Confirm roadmap</b> to save it. Confirm is available only when there are no blocking issues, and every confirmation is recorded.',
    '<b>Roadmap</b> opens the same view for the saved roadmap of any group. A confirmed group shows who confirmed it and when, and its Approved applications are locked.',
    'Toggle between <b>Table View</b> and <b>Gantt Chart</b> to review timing.',
    'Use <b>view</b> to inspect a row; <b>edit</b> to adjust dates &mdash; editing is only allowed while the app is in <b>Need Approval</b>.',
  ],
  fits: 'Integration &rarr; Implementation Planning &rarr; Roadmap Schedule',
  firstTime: 'Pick an integration group, then click Calculate Schedule to populate planned dates and phase timing.',
  sections: [
    {
      type: 'defs',
      label: 'Implementation phases',
      items: [
        { term: 'Assessment & Planning', color: '#BFDBFE', def: 'Scoping and preparation before design.' },
        { term: 'Design & Architecture', color: '#E9D5FF', def: 'Target design and architecture work.' },
        { term: 'Migration Execution', color: '#BBF7D0', def: 'The core migration/build effort.' },
        { term: 'Testing & Validation', color: '#FDE68A', def: 'Verification before go-live.' },
        { term: 'Go-Live and Support', color: '#FECACA', def: 'Cutover and post-launch tuning.' },
      ],
      note: 'A red vertical marker on the Gantt indicates today. The Gantt runs one column per day; weekends and US federal holidays are shaded as non-working days — the same days the schedule calculation skips.',
    },
  ],
};

interface IntegrationGroup {
  group_id: string;
  group_name: string;
  application_list: string;
  earliest_start_date: string;
  applications: string[];
}

interface ScheduleRecord {
  schedule_id: number;
  application_id: string;
  application_name: string;
  integration_group_id: string;
  integration_group_name: string;
  group_effective_from?: string;
  planned_start_date: string;
  planned_end_date: string;
  schedule_status: string;
  total_duration_days: number;
  total_effort_days: number;
  calculated_priority_score: number;
  phase_assessment_start: string;
  phase_assessment_end: string;
  phase_design_start: string;
  phase_design_end: string;
  phase_migration_start: string;
  phase_migration_end: string;
  phase_testing_start: string;
  phase_testing_end: string;
  phase_golive_start: string;
  phase_golive_end: string;
  resource_constraint_flag: number;
  resource_constraint_count?: number;
  dependency_constraint_flag: number;
  recommendation?: string;
}

interface GanttPhase {
  name: string;
  base_duration: number;
  buffer_days: number;
  total_duration: number;
  start_date: string;
  end_date: string;
}

interface GanttData {
  id: string;
  text: string;
  start_date: string;
  end_date: string;
  phases: GanttPhase[];
  schedule_status: string;
  application_status: string;
  integration_group_id: string;
  integration_group_name?: string;
  recommendation?: string;
}

// DW: phase colours ENCODE the five implementation phases on the Gantt bars and legend (and
// match the Help definitions), so they stay as data colours, not kit chrome.
// DEVIATION (owner request 2026-11-03, "colors to match the color of the phases … they should
// be softer"): the five phase hues of High-Level Implementation Roadmap
// (ImplementationRoadmap.tsx:92-123 — blue, purple, green, amber, coral), as SOFT pastel fills
// (the 200 step of each hue), FILL ONLY — owner, 2026-11-03: "I do not need a border line as a
// separate line … just fill". Known trade-off, accepted by the owner: a pastel fill alone is
// ~1.2–1.5:1 against white, below DESIGN.md's 3:1 for chart marks; the phase is also named
// in each bar's tooltip and in the legend.
const PHASE_COLORS: Record<string, { fill: string }> = {
  'Assessment & Planning': { fill: '#BFDBFE' },
  'Design & Architecture': { fill: '#E9D5FF' },
  'Migration Execution': { fill: '#BBF7D0' },
  'Testing & Validation': { fill: '#FDE68A' },
  'Go-Live & Optimization': { fill: '#FECACA' }
};
// Day separators (owner request 2026-11-03: "each day separated … by tiny grey dotted line").
const GANTT_DAY_RULE = '1px dotted #c8ccd1';

// Day-level Gantt geometry and non-working-day fills (owner request 2026-11-03).
const GANTT_DAY_PX = 22; // at 100% zoom
const GANTT_FIXED_COLS_PX = 402; // Grouping ID 180 + Application 220 + the 2px divider
const GANTT_HEADER_PX = 56;
// 50px, as on Roadmap Visualization: the Application column holds the ID with its disposition
// chip (24px) above the name (owner request 2026-11-04). Bars stay 24px, centred.
const GANTT_ROW_PX = 50;
const GANTT_BAR_PX = 24;
const GANTT_WEEKEND_FILL = '#efe9e1';
// Holidays: a lighter warm base (#f5e9da — gold bar 3.19:1, orange 3.48:1) with a thin hatch,
// so they read as different from weekends by pattern, not by a darker fill.
const GANTT_HOLIDAY_FILL = 'repeating-linear-gradient(135deg, #f5e9da 0 5px, #e3cfb6 5px 7px)';
const GANTT_HOLIDAY_INK = '#8c3a00'; // the kit's warning ink (CLAUDE.md 1.9.2)

const RoadmapSchedule: React.FC = () => {
  const [integrationGroups, setIntegrationGroups] = useState<IntegrationGroup[]>([]);
  // Several groups may be picked (Ctrl-click). The routes take one group or ALL, so several load ALL
  // and are narrowed here; the effect re-runs on the picked set itself, not just the fetch key.
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const selectedGroupId = groupFetchKey(selectedGroupIds);
  const pickedKey = selectedGroupIds.join('|');
  const keepPicked = <T extends { integration_group_id: string }>(rows: T[]) =>
    selectedGroupIds.length > 1 ? rows.filter(r => selectedGroupIds.includes(r.integration_group_id)) : rows;
  const [selectedGroup, setSelectedGroup] = useState<IntegrationGroup | null>(null);
  const [scheduleRecords, setScheduleRecords] = useState<ScheduleRecord[]>([]);
  const [ganttData, setGanttData] = useState<GanttData[]>([]);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'gantt' | 'review'>('table');
  // Owner request 2026-11-04: Calculate Schedule opens the Roadmap Review with the calculated
  // roadmap as a PROPOSAL; nothing is saved until the client confirms it there.
  const [reviewMode, setReviewMode] = useState<'proposal' | 'current'>('proposal');
  const [reviewKey, setReviewKey] = useState(0);
  const openReview = (mode: 'proposal' | 'current') => { setReviewMode(mode); setReviewKey((n) => n + 1); setViewMode('review'); };
  // Gantt zoom and start date — the High-Level Implementation Roadmap's controls
  // (ImplementationRoadmap.tsx:289-292, 716-719), plus Fit to screen (owner request 2026-11-03).
  const [zoomLevel, setZoomLevel] = useState(1);
  const [ganttStartDate, setGanttStartDate] = useState<string>('');
  const [ganttScroller, setGanttScroller] = useState<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<ScheduleRecord | null>(null);
  const [viewOnlyMode, setViewOnlyMode] = useState(false);

  // Track deployment statuses for all applications (from deployment_status table)
  const [deploymentStatuses, setDeploymentStatuses] = useState<Map<string, string>>(new Map());

  // Load integration groups and deployment statuses on mount
  useEffect(() => {
    fetchIntegrationGroups();
    fetchDeploymentStatuses();
  }, []);

  // Load schedule when group changes
  useEffect(() => {
    if (selectedGroupId) {
      if (selectedGroupId === 'ALL') {
        setSelectedGroup(null);
      } else {
        const group = integrationGroups.find(g => g.group_id === selectedGroupId);
        setSelectedGroup(group || null);
      }
      fetchScheduleData();
      if (viewMode === 'gantt') {
        fetchGanttData();
      }
    } else {
      setSelectedGroup(null);
      setScheduleRecords([]);
      setGanttData([]);
    }
  }, [pickedKey, viewMode, integrationGroups]);

  const fetchIntegrationGroups = async () => {
    try {
      const response = await authFetch('/api/integration-groups?exclude_analysis=true');
      const result = await response.json();
      if (result.success) {
        setIntegrationGroups(result.data || []);
      }
    } catch (err) {
      console.error('Error fetching integration groups:', err);
    }
  };

  // Fetch deployment statuses from deployment_status table
  const fetchDeploymentStatuses = async () => {
    try {
      const response = await authFetch('/api/deployment-status');
      const result = await response.json();
      // API returns array directly, not wrapped in { success, data }
      const dataArray = Array.isArray(result) ? result : (result.data || []);
      const statusMap = new Map<string, string>();
      dataArray.forEach((r: any) => {
        if (r.application_id && r.deployment_status) {
          // Keyed (group, app): an app in several groups (APP-0544) has a status per group.
          statusMap.set(`${r.integration_group_id}|${r.application_id}`, r.deployment_status);
        }
      });
      setDeploymentStatuses(statusMap);
    } catch (err) {
      console.error('Error fetching deployment statuses:', err);
    }
  };

  // Helper to get status color matching DispositionControl
  // DW: the status pairs are the kit's measured tones (amber → warning, blues → info,
  // green → success, grey → neutral).
  const getStatusColor = (status: string): WsTone => {
    switch (status) {
      case 'Need Approval': return 'warning';
      case 'Approved': return 'info';
      case 'In Development': return 'info';
      case 'Deployed': return 'success';
      default: return 'neutral';
    }
  };

  // Helper to check if editing is disabled (only editable for Need Approval status)
  const isEditDisabled = (applicationId: string, groupId: string) => {
    const status = deploymentStatuses.get(`${groupId}|${applicationId}`);
    return status && status !== 'Need Approval';
  };

  const fetchScheduleData = async () => {
    if (!selectedGroupId) return;

    setLoading(true);
    setError(null);

    // Always refresh deployment statuses when fetching schedule data
    fetchDeploymentStatuses();

    try {
      // If "ALL" is selected, fetch without group filter
      const url = selectedGroupId === 'ALL'
        ? '/api/roadmap-schedule'
        : `/api/roadmap-schedule?integration_group_id=${selectedGroupId}`;
      const response = await authFetch(url);
      const result = await response.json();

      if (result.success) {
        setScheduleRecords(keepPicked(result.data || []));
      } else {
        setError(result.error || 'Failed to fetch schedule data');
      }
    } catch (err: any) {
      setError(err.message || 'Error fetching schedule data');
    } finally {
      setLoading(false);
    }
  };

  const fetchGanttData = async () => {
    if (!selectedGroupId) return;

    try {
      // If "ALL" is selected, fetch without group filter
      const url = selectedGroupId === 'ALL'
        ? '/api/roadmap-schedule/visualization/gantt'
        : `/api/roadmap-schedule/visualization/gantt?integration_group_id=${selectedGroupId}`;
      const response = await authFetch(url);
      const result = await response.json();

      if (result.success) {
        setGanttData(keepPicked(result.data || []));
      }
    } catch (err) {
      console.error('Error fetching Gantt data:', err);
    }
  };

  const handleCalculateSchedule = async () => {
    if (!selectedGroupId) {
      setError('Please select an integration group first');
      return;
    }

    setCalculating(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await authFetch('/api/roadmap-schedule/calculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ integration_group_id: selectedGroupId })
      });

      const result = await response.json();

      if (result.success) {
        setSuccessMessage(`Schedule calculated successfully for ${result.count} applications`);
        fetchScheduleData();
        if (viewMode === 'gantt') {
          fetchGanttData();
        }
      } else {
        setError(result.error || 'Failed to calculate schedule');
      }
    } catch (err: any) {
      setError(err.message || 'Error calculating schedule');
    } finally {
      setCalculating(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  // Calculate Gantt chart dimensions
  const getGanttDimensions = useCallback(() => {
    if (ganttData.length === 0) return { minDate: new Date(), maxDate: new Date(), totalDays: 0 };

    // DEVIATION (owner request 2026-11-03, day-level Gantt): dates are read as LOCAL calendar
    // days. The source's `new Date("YYYY-MM-DD")` is UTC midnight, which lands on the previous
    // day west of Greenwich — invisible at month scale, a whole column off at day scale.
    const allDates = ganttData.flatMap(item => [
      parseDay(item.start_date),
      parseDay(item.end_date)
    ]);

    const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));

    minDate.setDate(minDate.getDate() - 7);
    maxDate.setDate(maxDate.getDate() + 14);

    // A chosen Start Date replaces the automatic one (ImplementationRoadmap.tsx:343-344).
    const start = ganttStartDate ? parseDay(ganttStartDate) : minDate;
    const end = maxDate > start ? maxDate : new Date(start.getFullYear(), start.getMonth(), start.getDate() + 30);

    // Inclusive of the last day: every day in the range gets a column.
    const totalDays = daysBetween(start, end) + 1;

    return { minDate: start, maxDate: end, totalDays };
  }, [ganttData, ganttStartDate]);

  const ZOOM_MIN = 0.1;
  const ZOOM_MAX = 3;
  const handleZoomIn = () => setZoomLevel(prev => Math.min(Math.round((prev + 0.25) * 4) / 4, ZOOM_MAX));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(Math.round((prev - 0.25) * 4) / 4, ZOOM_MIN));
  const handleZoomReset = () => setZoomLevel(1);
  // Fit to screen: the zoom at which every day fits the visible width beside the fixed columns.
  const handleFitToScreen = () => {
    const { totalDays } = getGanttDimensions();
    if (!ganttScroller || totalDays === 0) return;
    const available = ganttScroller.clientWidth - GANTT_FIXED_COLS_PX - 32;
    setZoomLevel(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, available / (totalDays * GANTT_DAY_PX))));
  };

  const renderGanttChart = () => {
    const { minDate, totalDays } = getGanttDimensions();
    // DEVIATION (owner request 2026-11-03): the source drew months only, at 3px a day. The chart
    // is now broken down BY DAY — one column per calendar day, weekends and the scheduler's
    // holidays shaded — so positions are whole-day pixel offsets instead of percentages.
    const DAY = GANTT_DAY_PX * zoomLevel;
    const chartWidth = totalDays * DAY;
    // Day labels need room: number + weekday from 16px a day, nothing narrower (shading stays).
    const showDayLabels = DAY >= 16;
    // Dotted day rules down to 6px a day; narrower than that they would merge into a grey wash.
    const showDayRules = DAY >= 6;
    const dayIndex = (dateStr: string) => daysBetween(minDate, parseDay(dateStr));

    const holidays = holidaysBetween(minDate, new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate() + totalDays));
    const days = Array.from({ length: totalDays }, (_, i) => {
      const date = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate() + i);
      const weekend = date.getDay() === 0 || date.getDay() === 6;
      const holiday = holidays.get(dayKey(date));
      return { date, weekend, holiday };
    });

    const monthHeaders: { month: string; startDay: number; dayCount: number }[] = [];
    days.forEach((d, i) => {
      const last = monthHeaders[monthHeaders.length - 1];
      if (i === 0 || d.date.getDate() === 1) {
        monthHeaders.push({ month: d.date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }), startDay: i, dayCount: 1 });
      } else if (last) {
        last.dayCount++;
      }
    });

    const todayIndex = daysBetween(minDate, new Date());
    const showTodayLine = todayIndex >= 0 && todayIndex < totalDays;

    // DW: Gantt geometry (column widths, 40px rows, 30px header, bar position and height) and the
    // phase colours are unchanged; only the chrome (text, borders, row fills) reads kit tokens.
    const ganttHeaderCell: React.CSSProperties = {
      padding: '6px 12px',
      ...wsType('meta'),
      fontWeight: 600,
      color: WS_COLOR.text,
      textAlign: 'center'
    };
    const ganttSubText: React.CSSProperties = {
      ...wsType('meta'),
      color: WS_COLOR.textMuted,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      textAlign: 'left'
    };

    return (
      <div ref={setGanttScroller} data-testid="gantt-scroller" style={{ overflowX: 'auto' }}>
        <div style={{ display: 'flex' }}>
          {/* Fixed Columns - Grouping ID and Application */}
          <div style={{ flexShrink: 0, borderRight: `2px solid ${WS_COLOR.border}`, position: 'sticky', left: 0, zIndex: 20, backgroundColor: WS_COLOR.surface }}>
            {/* Column Headers */}
            <div style={{ display: 'flex', height: `${GANTT_HEADER_PX}px`, borderBottom: `1px solid ${WS_COLOR.border}`, marginBottom: '8px', backgroundColor: WS_COLOR.surfaceSubtle }}>
              <div style={{
                width: '180px',
                ...ganttHeaderCell,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRight: `1px solid ${WS_COLOR.border}`
              }}>
                Grouping ID
              </div>
              <div style={{
                width: '220px',
                ...ganttHeaderCell,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                Application
              </div>
            </div>

            {/* Data Rows */}
            {ganttData.map((item, rowIdx) => (
              <div
                key={`info-${item.id}`}
                style={{
                  display: 'flex',
                  height: `${GANTT_ROW_PX}px`,
                  borderBottom: `1px solid ${WS_COLOR.border}`,
                  backgroundColor: rowIdx % 2 === 0 ? WS_COLOR.surfaceSubtle : WS_COLOR.surface
                }}
              >
                {/* Grouping ID Column */}
                <div style={{
                  width: '180px',
                  padding: '4px 12px',
                  borderRight: `1px solid ${WS_COLOR.border}`,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  textAlign: 'left'
                }}>
                  <div style={{ ...wsType('mono'), fontWeight: 600, color: WS_COLOR.text, textAlign: 'left' }}>
                    {item.integration_group_id}
                  </div>
                  {item.integration_group_name && (
                    <div style={ganttSubText}>
                      {item.integration_group_name}
                    </div>
                  )}
                </div>

                {/* Application Column */}
                <div style={{
                  width: '220px',
                  padding: '4px 12px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  textAlign: 'left'
                }}>
                  {/* Owner request 2026-11-04: the disposition chip after the ID, as on Roadmap
                      Visualization; the ID itself is black whatever the disposition. */}
                  <div style={{ ...wsType('mono'), fontWeight: 600, color: WS_COLOR.text, textAlign: 'left', display: 'flex', alignItems: 'center' }}>
                    {item.id}
                    <DispositionChip value={item.recommendation || scheduleRecords.find(r => r.application_id === item.id)?.recommendation} />
                  </div>
                  <div style={ganttSubText}>
                    {item.text}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Scrollable Gantt Chart Area */}
          <div style={{ flexShrink: 0, width: chartWidth, padding: '0 16px', boxSizing: 'content-box', position: 'relative' }}>
            {/* Day separators through every row: one dotted rule per day, drawn once over the
                rows — ABOVE the bars too (owner, 2026-11-03: "break the duration line by days"), so each
                bar reads as its days. Click-through, so the bar tooltips still work. */}
            {showDayRules && (
              <div aria-hidden style={{ position: 'absolute', left: 16, top: GANTT_HEADER_PX + 8, bottom: 0, width: chartWidth, zIndex: 3, pointerEvents: 'none' }}>
                {days.map((_, i) => (
                  <div key={`rule-${i}`} data-testid="gantt-day-rule" style={{ position: 'absolute', left: (i + 1) * DAY - 1, top: 0, bottom: 0, borderRight: GANTT_DAY_RULE }} />
                ))}
              </div>
            )}
            {/* Timeline header: months over days (day number + weekday initial) */}
            <div style={{ position: 'relative', height: `${GANTT_HEADER_PX}px`, borderBottom: `1px solid ${WS_COLOR.border}`, marginBottom: '8px' }}>
              {monthHeaders.map((mh, idx) => (
                <div
                  key={idx}
                  style={{
                    position: 'absolute',
                    left: mh.startDay * DAY,
                    width: mh.dayCount * DAY,
                    top: 0,
                    height: '26px',
                    boxSizing: 'border-box',
                    // Left-aligned so a month running past the visible edge still shows its name.
                    textAlign: 'left',
                    paddingLeft: '8px',
                    ...wsType('meta'),
                    fontWeight: 600,
                    color: WS_COLOR.text,
                    borderRight: `1px solid ${WS_COLOR.border}`,
                    borderBottom: `1px solid ${WS_COLOR.border}`,
                    paddingTop: '5px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden'
                  }}
                >
                  {mh.month}
                </div>
              ))}
              {days.map((d, i) => (
                <div
                  key={`d-${i}`}
                  data-testid="gantt-day"
                  data-nonworking={d.weekend || d.holiday ? 'true' : undefined}
                  title={d.holiday ? `${d.holiday} (non-working day)` : d.weekend ? 'Weekend (non-working day)' : undefined}
                  style={{
                    position: 'absolute',
                    left: i * DAY,
                    width: DAY,
                    top: '26px',
                    bottom: 0,
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1.1,
                    fontSize: '10px',
                    color: d.holiday ? GANTT_HOLIDAY_INK : WS_COLOR.textMuted,
                    fontWeight: d.holiday ? 700 : 400,
                    background: d.holiday ? GANTT_HOLIDAY_FILL : d.weekend ? GANTT_WEEKEND_FILL : 'transparent',
                    borderRight: d.date.getDay() === 0 ? `1px solid ${WS_COLOR.border}` : showDayRules ? GANTT_DAY_RULE : undefined
                  }}
                >
                  {showDayLabels && <span>{d.date.getDate()}</span>}
                  {showDayLabels && <span>{'SMTWTFS'[d.date.getDay()]}</span>}
                </div>
              ))}
            </div>

            {/* Gantt Rows */}
            {ganttData.map((item, rowIdx) => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  height: `${GANTT_ROW_PX}px`,
                  borderBottom: `1px solid ${WS_COLOR.border}`,
                  backgroundColor: rowIdx % 2 === 0 ? WS_COLOR.surfaceSubtle : WS_COLOR.surface
                }}
              >
                {/* overflow hidden: a bar that starts before a chosen Start Date is cut at the edge */}
                <div style={{ flex: 1, position: 'relative', height: '100%', overflow: 'hidden' }}>
                {/* Non-working days: weekends and the scheduler's holidays, shaded through the row */}
                {days.map((d, i) => (d.weekend || d.holiday) ? (
                  <div
                    key={`nw-${i}`}
                    style={{
                      position: 'absolute',
                      left: i * DAY,
                      width: DAY,
                      top: 0,
                      bottom: 0,
                      background: d.holiday ? GANTT_HOLIDAY_FILL : GANTT_WEEKEND_FILL
                    }}
                  />
                ) : null)}

                {showTodayLine && (
                  <div
                    style={{
                      position: 'absolute',
                      left: todayIndex * DAY + DAY / 2 - 1,
                      top: 0,
                      bottom: 0,
                      width: '2px',
                      // DW: the today marker stays red — the kit's error ink.
                      backgroundColor: WS_STATUS.error.ink,
                      zIndex: 10
                    }}
                  />
                )}

                {item.phases.map((phase, phaseIdx) => {
                  if (!phase.start_date || !phase.end_date) return null;
                  const start = dayIndex(phase.start_date);
                  // Inclusive of the end day: a phase ending on a Friday covers that Friday.
                  const span = Math.max(1, dayIndex(phase.end_date) - start + 1);

                  return (
                    <div
                      key={phaseIdx}
                      data-testid="gantt-bar"
                      title={`${phaseLabel(phase.name)}\n${formatDate(phase.start_date)} - ${formatDate(phase.end_date)}`}
                      style={{
                        position: 'absolute',
                        left: start * DAY,
                        width: span * DAY,
                        top: `${(GANTT_ROW_PX - GANTT_BAR_PX) / 2}px`,
                        height: `${GANTT_BAR_PX}px`,
                        boxSizing: 'border-box',
                        backgroundColor: PHASE_COLORS[phase.name]?.fill || WS_COLOR.textMuted,
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        zIndex: 2,
                        // Owner request 2026-11-04: the phase name, centred in its bar
                        // ("Assessment" for Assessment & Planning, the shortest phase); cut with
                        // an ellipsis when the bar is narrower than the name at this zoom.
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        padding: '0 4px'
                      }}
                    >
                      {span * DAY >= 28 && (
                        <span data-testid="gantt-bar-label" style={{ ...wsType('meta'), fontWeight: 500, color: WS_COLOR.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {phase.name === 'Assessment & Planning' ? 'Assessment' : phaseLabel(phase.name)}
                        </span>
                      )}
                    </div>
                  );
                })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          marginTop: '16px',
          padding: '12px',
          backgroundColor: WS_COLOR.surfaceSubtle,
          borderRadius: `${WS_RADIUS.card}px`
        }}>
          {Object.entries(PHASE_COLORS).map(([name, color]) => (
            <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '16px', height: '16px', backgroundColor: color.fill, borderRadius: '4px' }} />
              <span style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>{phaseLabel(name)}</span>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '16px', height: '16px', backgroundColor: GANTT_WEEKEND_FILL, border: `1px solid ${WS_COLOR.border}`, borderRadius: '4px' }} />
            <span style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>Weekend</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '16px', height: '16px', background: GANTT_HOLIDAY_FILL, border: `1px solid ${WS_COLOR.border}`, borderRadius: '4px' }} />
            <span style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>US federal holiday (non-working)</span>
          </div>
        </div>
      </div>
    );
  };

  // Table styles matching the screenshot
  // DW: header and cell type, colours and borders read kit tokens; padding and widths unchanged.
  const tableStyles = {
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const,
      ...wsType('table'),
      backgroundColor: WS_COLOR.surface
    },
    headerRow: {
      backgroundColor: WS_COLOR.surfaceSubtle,
      borderBottom: `1px solid ${WS_COLOR.border}`
    },
    headerCell: {
      padding: '14px 12px',
      textAlign: 'left' as const,
      ...wsType('tableHeader'),
      color: WS_COLOR.text,
      background: WS_COLOR.surfaceSubtle,
      borderBottom: `1px solid ${WS_COLOR.border}`,
      whiteSpace: 'nowrap' as const
    },
    headerCellCenter: {
      padding: '14px 12px',
      textAlign: 'center' as const,
      ...wsType('tableHeader'),
      color: WS_COLOR.text,
      background: WS_COLOR.surfaceSubtle,
      borderBottom: `1px solid ${WS_COLOR.border}`,
      whiteSpace: 'nowrap' as const
    },
    bodyRow: {
      borderBottom: `1px solid ${WS_COLOR.border}`
    },
    cell: {
      padding: '16px 12px',
      textAlign: 'left' as const,
      verticalAlign: 'top' as const,
      borderBottom: `1px solid ${WS_COLOR.border}`,
      ...wsType('table')
    },
    cellCenter: {
      padding: '16px 12px',
      textAlign: 'center' as const,
      verticalAlign: 'middle' as const,
      borderBottom: `1px solid ${WS_COLOR.border}`,
      ...wsType('table')
    }
  };

  const emptyStateStyle: React.CSSProperties = { padding: '40px', textAlign: 'center', color: WS_COLOR.textMuted };
  const emptyIconStyle: React.CSSProperties = { display: 'flex', justifyContent: 'center', marginBottom: '16px', color: WS_COLOR.textMuted };
  const emptyTextStyle: React.CSSProperties = { color: WS_COLOR.textMuted, ...wsType('table') };

  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <HelpPanel help={RoadmapScheduleHelp} />
      {/* Header */}
      {/* Owner request 2026-11-04: Calculate Schedule sits in the window heading, the only one on
          the screen. It opens the review with the calculated roadmap as a proposal. */}
      <PageHeader title={<>Roadmap Schedule</>} subtitle={<>Plan and manage application schedules across integration groups</>}
        actions={
          <button
            className="btn btn-primary"
            onClick={() => openReview('proposal')}
            disabled={!selectedGroupIds.length}
            data-testid="calculate-schedule"
            title="Calculate a proposed roadmap from effort, dependencies and the group start date"
          >
            <Icon icon={icons.Calculator} size={20} /> Calculate Schedule
          </button>
        } />

      {/* Controls Card - Sticky below header */}
      <div style={{ flexShrink: 0, padding: '0 16px', marginBottom: '16px' }}>
        <div className="md-card">
          <div className="md-card-content" style={{ padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              {/* Integration Group Selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <GroupMultiSelect
                  testId="group-picker"
                  label="Integration Group:"
                  placeholder="Select Integration Group..."
                  groups={integrationGroups.map(group => ({ id: group.group_id, description: `${group.group_name} (${group.applications?.length || 0} apps)` }))}
                  value={selectedGroupIds}
                  onChange={setSelectedGroupIds}
                />
              </div>

              {/* View Mode Toggle */}
              {/* DW: the segmented toggle uses the module's view-btn (tonal; the active one filled). */}
              <div style={{ display: 'flex', gap: '4px' }}>
                <button
                  className={`view-btn${viewMode === 'table' ? ' active' : ''}`}
                  onClick={() => setViewMode('table')}
                >
                  Table View
                </button>
                <button
                  className={`view-btn${viewMode === 'gantt' ? ' active' : ''}`}
                  onClick={() => setViewMode('gantt')}
                >
                  Gantt Chart
                </button>
                <button
                  className={`view-btn${viewMode === 'review' ? ' active' : ''}`}
                  onClick={() => openReview('current')}
                  disabled={!selectedGroupIds.length}
                  data-testid="view-review"
                  title="The roadmap view: timeline, dependencies, feasibility and confirmation — for confirmed and unconfirmed groups alike"
                >
                  Roadmap
                </button>
              </div>

              {/* Start Date + Zoom (Gantt only) — the High-Level Implementation Roadmap's controls
                  (ImplementationRoadmap.tsx:1115-1155), plus Fit to screen. */}
              {viewMode === 'gantt' && (
                <div className="rs-gantt-controls" data-testid="gantt-controls">
                  <label>Start Date:</label>
                  <input
                    type="date"
                    value={ganttStartDate}
                    onChange={(e) => setGanttStartDate(e.target.value)}
                    aria-label="Gantt start date"
                    style={{ minWidth: '150px' }}
                  />
                  {ganttStartDate && (
                    <button onClick={() => setGanttStartDate('')} className="zoom-btn reset" title="Reset to auto-detect">
                      Reset
                    </button>
                  )}
                  <label style={{ marginLeft: '8px' }}>Zoom:</label>
                  <div className="zoom-controls">
                    <button onClick={handleZoomOut} className="zoom-btn" disabled={zoomLevel <= ZOOM_MIN} aria-label="Zoom out">
                      <Icon icon={icons.Minus} size={20} />
                    </button>
                    <span className="zoom-level" data-testid="zoom-level">{Math.round(zoomLevel * 100)}%</span>
                    <button onClick={handleZoomIn} className="zoom-btn" disabled={zoomLevel >= ZOOM_MAX} aria-label="Zoom in">
                      <Icon icon={icons.Plus} size={20} />
                    </button>
                    <button onClick={handleFitToScreen} className="zoom-btn reset" title="Show the whole schedule in the visible width">
                      Fit to screen
                    </button>
                    <button onClick={handleZoomReset} className="zoom-btn reset">
                      Reset
                    </button>
                  </div>
                </div>
              )}

              {/* Stats */}
              {scheduleRecords.length > 0 && (
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '16px', ...wsType('label'), color: WS_COLOR.textMuted }}>
                  <span><strong>{scheduleRecords.length}</strong> applications</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Data Content */}
      {viewMode === 'review' && selectedGroupIds.length > 0 ? (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '0 16px 16px 16px' }}>
          <RoadmapReview
            key={reviewKey}
            groupIds={selectedGroupIds}
            mode={reviewMode}
            onClose={() => setViewMode('table')}
            onConfirmed={() => { fetchScheduleData(); fetchDeploymentStatuses(); }}
          />
        </div>
      ) : (
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px 16px' }}>
        {/* Messages */}
        {error && (
          <div style={{ marginBottom: '16px' }}>
            <Banner tone="error">
              {error}
            </Banner>
          </div>
        )}

        {successMessage && (
          <div style={{ marginBottom: '16px' }}>
            <Banner tone="success">
              {successMessage}
            </Banner>
          </div>
        )}

        {/* Main Content */}
        <div className="md-card">
          <div className="md-card-content" style={{ padding: '0' }}>
            {loading ? (
              <div style={emptyStateStyle}>
                Loading schedule data...
              </div>
            ) : !selectedGroupId ? (
              <div style={emptyStateStyle}>
                <div style={emptyIconStyle}><Icon icon={icons.Calendar} size={24} /></div>
                <p style={emptyTextStyle}>
                  Select an integration group to view or calculate the roadmap schedule
                </p>
              </div>
            ) : viewMode === 'table' ? (
              /* Table View - Matching Screenshot Layout */
              scheduleRecords.length === 0 ? (
                <div style={emptyStateStyle}>
                  <div style={emptyIconStyle}><Icon icon={icons.BarChart3} size={24} /></div>
                  <p style={{ ...emptyTextStyle, marginBottom: '16px' }}>
                    No schedule data found for this integration group — use Calculate Schedule at the top of the screen.
                  </p>
                </div>
              ) : (
                <div className="roadmap-table-container" data-testid="schedule-table" tabIndex={0} role="region" aria-label="Roadmap schedule table">
                  <table className="roadmap-table">
                    <thead>
                      <tr>
                        <th rowSpan={2} className="sticky-col col-grouping">Grouping</th>
                        <th rowSpan={2} className="sticky-col col-application">Application</th>
                        <th rowSpan={2} className="two-line-header"><div>Grouping</div><div>Dates</div></th>
                        <th rowSpan={2} className="two-line-header"><div>Start</div><div>Date</div></th>
                        <th rowSpan={2} className="two-line-header"><div>End</div><div>Date</div></th>
                        <th rowSpan={2} className="two-line-header"><div>Duration</div><div>(Calendar</div><div>Days)</div></th>
                        <th rowSpan={2} className="two-line-header"><div>Duration</div><div>(Business</div><div>Days)</div></th>
                        <th rowSpan={2} className="two-line-header"><div>Effort</div><div>(Days)</div></th>
                        {RV_PHASES.map((p) => (
                          <th key={p.key} colSpan={3} className="phase-header" style={{ backgroundColor: RV_PHASE_COLORS[p.name]?.primary, color: WS_COLOR.onPrimary }}>{phaseLabel(p.name)}</th>
                        ))}
                        <th rowSpan={2}>Status</th>
                        <th rowSpan={2}>Actions</th>
                      </tr>
                      <tr>
                        {RV_PHASES.map((p) => (
                          <React.Fragment key={`${p.key}-sub`}>
                            {['Duration', 'Start Date', 'End Date'].map((h) => (
                              <th key={h} className="sub-header" style={{ backgroundColor: RV_PHASE_COLORS[p.name]?.bg }}>{h}</th>
                            ))}
                          </React.Fragment>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[...scheduleRecords].sort((a, b) => a.integration_group_id.localeCompare(b.integration_group_id) || String(a.planned_start_date).localeCompare(String(b.planned_start_date)) || a.application_id.localeCompare(b.application_id)).map((record) => {
                        const d10 = (v?: string | null) => (v ? String(v).slice(0, 10) : null);
                        const start = d10(record.planned_start_date);
                        const end = d10(record.planned_end_date);
                        const bdTotal = RV_PHASES.reduce((sum, p) => sum + (businessDays(d10((record as any)[`phase_${p.key}_start`]), d10((record as any)[`phase_${p.key}_end`])) || 0), 0);
                        const status = deploymentStatuses.get(`${record.integration_group_id}|${record.application_id}`) || 'Not Set';
                        return (
                          <tr key={`${record.integration_group_id}|${record.application_id}`} className={status === 'Deployed' ? 'completed-row' : ''}>
                            <td className="sticky-col col-grouping stacked-cell">
                              <div className="stacked-id">{record.integration_group_id}</div>
                              {record.integration_group_name && <div className="stacked-name" title={record.integration_group_name}>{record.integration_group_name}</div>}
                            </td>
                            <td className="sticky-col col-application stacked-cell">
                              <div className="stacked-id" style={{ display: 'flex', alignItems: 'center' }}>
                                {record.application_id}
                                <DispositionChip value={record.recommendation} />
                              </div>
                              {record.application_name && <div className="stacked-name" title={record.application_name}>{record.application_name}</div>}
                            </td>
                            <td className="date-cell" style={{ whiteSpace: 'nowrap' }}>
                              {fmtMDY(d10(record.group_effective_from || selectedGroup?.earliest_start_date || null))}<br />
                              <span style={{ color: WS_COLOR.textMuted }}>to</span> {fmtMDY(end)}
                            </td>
                            <td className="date-cell">{fmtMDY(start)}</td>
                            <td className="date-cell">{fmtMDY(end)}</td>
                            <td className="number-cell">{calendarDays(start, end) ?? '-'}</td>
                            <td className="number-cell">{bdTotal || '-'}</td>
                            <td className="number-cell">{record.total_effort_days ?? '-'}</td>
                            {RV_PHASES.map((p) => {
                              const bg = RV_PHASE_COLORS[p.name]?.bg;
                              const ps = d10((record as any)[`phase_${p.key}_start`]);
                              const pe = d10((record as any)[`phase_${p.key}_end`]);
                              return (
                                <React.Fragment key={p.key}>
                                  <td className="number-cell" style={{ backgroundColor: bg }}>{businessDays(ps, pe) ?? '-'}</td>
                                  <td className="date-cell" style={{ backgroundColor: bg }}>{fmtMDY(ps)}</td>
                                  <td className="date-cell" style={{ backgroundColor: bg }}>{fmtMDY(pe)}</td>
                                </React.Fragment>
                              );
                            })}
                            <td style={{ textAlign: 'center' }}>
                              <span style={{ ...WS_CHIP, ...wsTone(getStatusColor(status)) }}>{status}</span>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                                <IconButton icon={icons.Eye} label="View details" title="View details"
                                  onClick={() => { setEditingRecord(record); setViewOnlyMode(true); setEditModalOpen(true); }} />
                                {!isEditDisabled(record.application_id, record.integration_group_id) && (
                                  <IconButton icon={icons.Pencil} label="Edit schedule" title="Edit schedule"
                                    onClick={() => { setEditingRecord(record); setViewOnlyMode(false); setEditModalOpen(true); }} />
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              /* Gantt Chart View */
              ganttData.length === 0 ? (
                <div style={emptyStateStyle}>
                  <div style={emptyIconStyle}><Icon icon={icons.BarChart3} size={24} /></div>
                  <p style={{ ...emptyTextStyle, marginBottom: '16px' }}>
                    No schedule data to display in Gantt chart — use Calculate Schedule at the top of the screen.
                  </p>
                </div>
              ) : (
                renderGanttChart()
              )
            )}
          </div>
        </div>
      </div>
      )}

      {/* Edit Schedule Modal */}
      {/* DW: EditScheduleModal now renders as a right-side panel (kit Sheet). */}
      {editingRecord && (
        <EditScheduleModal
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setEditingRecord(null);
            setViewOnlyMode(false);
          }}
          onSave={() => {
            // Refresh data after save
            fetchScheduleData();
            fetchDeploymentStatuses();
            if (viewMode === 'gantt') {
              fetchGanttData();
            }
            setSuccessMessage('Schedule updated successfully');
            setTimeout(() => setSuccessMessage(null), 3000);
          }}
          applicationId={editingRecord.application_id}
          applicationName={editingRecord.application_name}
          integrationGroupId={editingRecord.integration_group_id}
          currentStartDate={editingRecord.planned_start_date}
          currentEndDate={editingRecord.planned_end_date}
          viewOnly={viewOnlyMode}
        />
      )}
    </div>
  );
};

export default RoadmapSchedule;
